import { Input } from 'antd';
import styled from '@emotion/styled';
import { ReactNode } from 'react';

export interface TextWithImageInputProps {
  /** 受控：文本内容 */
  text: string;
  /** 受控：图片 URL 列表 */
  images: string[];
  /** 文本变更回调 */
  onTextChange: (text: string) => void;
  /** 图片列表变更回调 */
  onImagesChange: (images: string[]) => void;
  /** 占位符 */
  placeholder?: string;
  /** 最大字数，默认 600 */
  maxLength?: number;
  /** 禁用 */
  disabled?: boolean;
  /** 错误状态（红色边框） */
  error?: boolean;
  /** 是否展示字数统计，默认 true */
  showCount?: boolean;
  /** TextArea autoSize，默认 { minRows: 2 } */
  autoSize?: { minRows?: number; maxRows?: number };
  /** 透传给 Input.TextArea 的额外属性（如 onBlur、快捷键等） */
  textareaProps?: React.ComponentProps<typeof Input.TextArea>;
  /** 渲染图片上传区域，由调用方注入（如 CustomUpload） */
  renderImageUpload: (props: {
    value: string[];
    onChange: (v: string[]) => void;
    disabled?: boolean;
  }) => ReactNode;
  /** 粘贴图片时调用，由调用方决定上传逻辑（如 handUpload） */
  onPasteImage?: (file: File) => void;
}

const Container = styled.div<{ disabled?: boolean; error?: boolean }>`
  border: 1px solid ${({ error }) => (error ? '#f5222d' : 'rgb(217, 217, 217)')};
  border-radius: 4px;
  ${({ disabled }) =>
    disabled
      ? `
    color: rgba(0,0,0,.25);
    background: #f5f5f5;
    cursor: not-allowed;
  `
      : ''}
  &:hover {
    border-color: ${({ disabled, error }) =>
      error ? '#f5222d' : disabled ? 'rgb(217, 217, 217)' : '#5c8aff'};
  }
  &:focus-within {
    box-shadow: 0 0 0 2px rgb(217, 217, 217);
  }
`;

/**
 * 文本 + 图片上传的编辑组件（公共组件）。
 * 不依赖具体业务数据格式，由调用方通过 text/images 与 renderImageUpload、onPasteImage 接入。
 */
const TextWithImageInput = (props: TextWithImageInputProps) => {
  const {
    text,
    images,
    onTextChange,
    onImagesChange,
    placeholder,
    maxLength = 600,
    disabled = false,
    error = false,
    showCount = true,
    autoSize = { minRows: 2 },
    textareaProps,
    renderImageUpload,
    onPasteImage,
  } = props;

  const handlePaste = (e: React.ClipboardEvent<HTMLTextAreaElement>) => {
    if (e.target !== document.activeElement) return;
    const file = e.clipboardData.items[0]?.getAsFile();
    if (file instanceof File && file.type.startsWith('image')) {
      onPasteImage?.(file);
      e.preventDefault();
    }
  };

  return (
    <Container disabled={disabled} error={error}>
      <div>
        <Input.TextArea
          onPaste={handlePaste}
          placeholder={placeholder}
          disabled={disabled}
          value={text}
          onChange={({ target: { value } }) => onTextChange(value)}
          bordered={false}
          maxLength={maxLength}
          autoSize={autoSize}
          {...textareaProps}
        />
      </div>
      <div className="flex items-end justify-between p-[12px] pt-[0]">
        {renderImageUpload({ value: images, onChange: onImagesChange, disabled })}
        {showCount && (
          <div className="text-[#B5B9BF]">
            {text?.length ?? 0}/{maxLength}
          </div>
        )}
      </div>
    </Container>
  );
};

export default TextWithImageInput;
